import type {
  BalanceKpis,
  BalanceRegistration,
  BalanceSlot,
} from '@sportninja/common/services/teamBalance'
import { isPlayerLine } from './balanceRowKind'

/**
 * SN-4709 display math. The API speaks DOLLARS end-to-end; Price renders minor
 * units — this is the single conversion boundary (display only).
 */
export const dollarsToMinor = (dollars: number): number =>
  Math.round(dollars * 100)

/**
 * Offline money RECORDED against this slot but not yet VERIFIED (D39), in minor
 * units.
 *
 * `slot.paid_in_person` is a server aggregate over verified, non-voided batches
 * only (`whereNotNull('op.verified_at')`), so a freshly recorded batch moves no
 * money on the sheet until a qualified verifier settles it. The POS/Cash column
 * surfaces this remainder as a separate chip — visible, but never summed into
 * TOTAL PAID or BALANCE DUE.
 *
 * The exact 'pending' match is deliberate: verification_status precedence is
 * voided > verified > pending, so a batch voided while pending reads 'voided'
 * and drops out here, which is correct — it is never coming.
 */
export const pendingOfflineMinor = (slot: BalanceSlot): number =>
  (slot.offline_payments ?? []).reduce(
    (sum, batch) =>
      batch.verification_status === 'pending'
        ? sum + dollarsToMinor(batch.amount)
        : sum,
    0
  )

/**
 * SN-4709 (James, Aug 12) — the EVEN SPLIT reference.
 *
 * James removed the even-split COLUMN ("it's actually not real, real data; it's
 * just a reference"), so the same figure now has to be STATED once — in the
 * toolbar beside Split Evenly and in the Player Share header tooltip. This is
 * the single derivation behind both, so the two can never quote different
 * numbers at each other.
 *
 * It is the SERVER's `slot.even_split` (`TeamBalanceService::decorateEvenSplit`,
 * the same penny-exact `AllocationMath::distributeCents` the re-split applies),
 * never a client `team_cost / count`. Re-deriving it here would put the advisory
 * and the button that applies it on two different implementations, and would be
 * cents wrong on any fee that does not divide evenly.
 *
 * The FIRST eligible slot's share, not an average: distributeCents hands the
 * remainder pennies to the leading slots, so shares differ by at most a cent and
 * the first is the figure the modal would actually write. Cancelled slots are
 * excluded from the split and carry null, so they are skipped rather than read
 * as $0.
 *
 * Single registration only — callers pass null on the All-Divisions view, where
 * one "per player" figure across several fees would be meaningless.
 */
export const evenSplitReference = (
  registration: BalanceRegistration | null | undefined
): number | null => {
  if (!registration) {
    return null
  }
  for (const slot of registration.slots) {
    // Sep 23 — an official's line has no even share (the server sends null); skipped
    // outright so an older payload can never quote one.
    if (!isPlayerLine(slot)) {
      continue
    }
    const share = slot.even_split
    if (share !== null && share !== undefined) {
      return share
    }
  }

  return null
}

/**
 * Currency codes across the visible registrations. Aggregate money KPIs may
 * only ever render when exactly ONE currency is present — a CAD+USD sum is
 * meaningless and must never be shown.
 */
export const uniqueCurrencyCodes = (
  registrations: readonly BalanceRegistration[]
): string[] => {
  return Array.from(
    new Set(
      registrations
        .map((registration) => registration.currency?.code)
        .filter((code): code is string => Boolean(code))
    )
  )
}

/**
 * Sum KPIs across registrations for the All-Divisions view. Callers must gate
 * on uniqueCurrencyCodes(...).length === 1 first. threshold_met intentionally
 * requires EVERY registration with a threshold to have met it.
 *
 * Tax on top: the cash figures (`expected_total`, `expected_tax`) are summed like
 * every other amount; `threshold_total` sums the registrations that have a
 * threshold and stays null when none does.
 */
export const sumKpis = (
  registrations: readonly BalanceRegistration[]
): BalanceKpis => {
  const zero: BalanceKpis = {
    allocated: 0,
    unallocated: 0,
    outstanding: 0,
    paid_online: 0,
    paid_online_7d: 0,
    paid_in_person: 0,
    total_paid: 0,
    threshold_met: registrations.length > 0,
    expected_total: 0,
    expected_tax: 0,
    threshold_total: null,
  }

  return registrations.reduce((totals, registration) => {
    const kpis = registration.kpis
    return {
      allocated: round2(totals.allocated + kpis.allocated),
      unallocated: round2(totals.unallocated + kpis.unallocated),
      outstanding: round2(totals.outstanding + kpis.outstanding),
      paid_online: round2(totals.paid_online + kpis.paid_online),
      paid_online_7d: round2(totals.paid_online_7d + kpis.paid_online_7d),
      paid_in_person: round2(totals.paid_in_person + kpis.paid_in_person),
      total_paid: round2(totals.total_paid + kpis.total_paid),
      threshold_met:
        totals.threshold_met &&
        (registration.threshold === null || kpis.threshold_met),
      expected_total: round2(
        (totals.expected_total ?? 0) + (kpis.expected_total ?? 0)
      ),
      expected_tax: round2(
        (totals.expected_tax ?? 0) + (kpis.expected_tax ?? 0)
      ),
      threshold_total:
        kpis.threshold_total === null || kpis.threshold_total === undefined
          ? totals.threshold_total ?? null
          : round2((totals.threshold_total ?? 0) + kpis.threshold_total),
    }
  }, zero)
}

/**
 * Total team cost across registrations (single-currency contexts only).
 */
export const sumTeamCost = (
  registrations: readonly BalanceRegistration[]
): number =>
  round2(
    registrations.reduce(
      (total, registration) => total + (registration.team_cost ?? 0),
      0
    )
  )

const round2 = (value: number): number => Math.round(value * 100) / 100
